"use client";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { isSpecializedWorkflow } from "@/utils/workflowUtils";
import { MIN_CHAT_WIDTH } from "@/utils/panelWidth";
import { MIN_PROMPT_HEIGHT } from "@/utils/promptHeight";
import { subscribeAndInvoke } from "@/utils/chat";
import { usePersistentResizablePanel } from "@/hooks/usePersistentResizablePanel";
import { useResizablePromptArea } from "@/hooks/useResizablePromptArea";
import { useChatStore, UploadedFile } from "@/stores/ChatStore";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useArtefactStore } from "@/stores/ArtefactStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { useResponsive } from "@/hooks/use-responsive";
import { useChatURLState } from "@/hooks/useChatURLState";
import { useToolPreferenceSync } from "@/hooks/useToolPreferenceSync";
import { logger } from "@/telemetry";
import { StructuredMention } from "@/utils/mentionParser";

import { PromptWithActions } from "./PromptWithActions";
import { ChatPanel } from "./ChatPanel";
import { FeatureCardProps } from "./FeatureCards";
import { PromptSuggestion } from "./PromptSuggestions";
import { ArtefactsPanel } from "./ArtefactsPanel";
import { ArtefactModal } from "./ArtefactModal";
import { ChatLoadingState } from "./ChatLoadingState";
import { WorkflowDescriptionBubble } from "./WorkflowDescriptionBubble";
import { ResizeHandle } from "./ResizeHandle";
import { PromptResizeHandle } from "./PromptResizeHandle";
import { ArtefactsPanelToggle } from "./ArtefactsPanelToggle";
import { InvestAIHeaderControls } from "./InvestAIHeaderControls";
import { IntegrationWarningBanner } from "./IntegrationWarningBanner";

interface ChatMessageWithTools {
  toolCallExecutions?: Array<{ name?: string }>;
  tool_call_executions?: Array<{ name?: string }>;
  toolCalls?: Array<{ function?: { name?: string }; name?: string }>;
  tool_calls?: Array<{ function?: { name?: string }; name?: string }>;
}

interface ChatProps {
  workflowId: string;
}

const initialSuggestions: PromptSuggestion[] = [];

export const Chat: React.FC<ChatProps> = ({ workflowId = "chat_agent" }) => {
  const { t } = useTranslation();
  const { userProfile } = useUser();
  const { brand } = useBrand();

  // UIPreferences are consumed inside PromptWithActions; we import the hook here
  // so that the store is initialised before the child renders.
  useUIPreferences();
  const { adminClient, platformClient, sseWorkflowClient } = useWebuildAPIClient();
  const { isMobile, isTablet } = useResponsive();

  useToolPreferenceSync();
  const {
    create,
    invoke,
    subscribe,
    runId,
    chat,
    chatWorkflowId,
    newChat,
    streaming,
    setCurrentWorkflowId,
    load,
    setOnChatSaved,
  } = useChatStore();
  const {
    artefactsVisible,
    toggleArtefacts,
    getFilteredArtefactsForCurrentChat,
    getCitationsForCurrentChat,
  } = useArtefactStore();
  const { fetchChatHistory, selectChat } = useChatHistoryStore();
  const loadWorkflow = useWorkflowStore((s) => s.loadWorkflow);
  const workflow = useWorkflowStore((s) => s.workflow);
  const isLoadingWorkflow = useWorkflowStore((s) => s.isLoadingWorkflow);

  // Phase 8: URL state management for chat history
  const { chatId: chatIdFromURL, navigateToChat } = useChatURLState();

  // Track the last chat ID we handled from URL to avoid re-processing on click navigation
  // When user clicks a chat in sidebar, SidebarLayout already handles selection before URL changes
  const lastHandledUrlChatIdRef = useRef<string | null>(null);

  // Resizable functionality for chat/artefacts boundary
  const { chatWidth, isResizing, containerRef, handleMouseDown, handleKeyboardResize } =
    usePersistentResizablePanel();

  // Resizable functionality for prompt input area height
  const {
    promptHeight,
    isResizingPrompt,
    promptWrapperRef,
    handlePromptResizeMouseDown,
    handlePromptResizeKeyboard,
    resetPromptHeight,
    maxPromptHeight,
  } = useResizablePromptArea();

  const initialFeaturesCategories: FeatureCardProps[] = useMemo(() => [], []);

  const [featuresCategories, setFeaturesCategories] =
    useState<FeatureCardProps[]>(initialFeaturesCategories);
  const [suggestions] = useState<PromptSuggestion[]>(initialSuggestions);
  const [dismissedIntegrationWarning, setDismissedIntegrationWarning] = useState(false);

  // Extract enabled tool IDs from workflow AND chat history for integration warning banner
  const enabledToolIds = useMemo(() => {
    const toolIds: string[] = [];

    // 1. Get tools from workflow configuration (YAML)
    if (workflow?.agents) {
      Object.values(workflow.agents).forEach((agent) => {
        const agentTools = agent.parameters?.tools || [];

        agentTools.forEach((toolId: string) => {
          if (!toolIds.includes(toolId)) {
            toolIds.push(toolId);
          }
        });
      });
    }

    // 2. Get tools from chat history (tools actually used in this session)
    if (chat?.messages) {
      chat.messages.forEach((message) => {
        const msg = message as ChatMessageWithTools;
        // Check for tool executions (the actual tools that were called)
        const executions = msg.toolCallExecutions || msg.tool_call_executions || [];

        if (Array.isArray(executions) && executions.length > 0) {
          executions.forEach((execution) => {
            const toolId = execution.name;

            if (toolId && !toolIds.includes(toolId)) {
              toolIds.push(toolId);
            }
          });
        }

        // Also check toolCalls for tools that might not have executed yet
        const calls = msg.toolCalls || msg.tool_calls || [];

        if (Array.isArray(calls) && calls.length > 0) {
          calls.forEach((toolCall) => {
            const toolId = toolCall.function?.name || toolCall.name;

            if (toolId && !toolIds.includes(toolId)) {
              toolIds.push(toolId);
            }
          });
        }
      });
    }

    return toolIds;
  }, [workflow?.agents, chat?.messages]);

  // Auto-hide artefacts panel on mobile — only react to isMobile changes
  useEffect(() => {
    if (isMobile && artefactsVisible) {
      toggleArtefacts();
    }
    // TODO: Refactor artefact visibility logic to avoid suppressing exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only reacts to isMobile changes; adding artefactsVisible would immediately close artefacts when opened on mobile
  }, [isMobile]);

  if (chatWorkflowId && chatWorkflowId !== workflowId) {
    newChat();
  }

  useEffect(() => {
    if (chat?.messages && chat.messages.length > 0) {
      setFeaturesCategories([]);
    } else {
      setFeaturesCategories(initialFeaturesCategories);
    }
  }, [chat?.messages, initialFeaturesCategories]);

  useEffect(() => {
    if (workflowId && adminClient) {
      // If workflow changed, force complete state clearing
      if (workflow && workflow.id !== workflowId) {
        logger.info("🔄 Chat useEffect - workflow changed, forcing complete state clear", {
          fromWorkflowId: workflow.id,
          toWorkflowId: workflowId,
        });

        // Force complete state clearing when switching workflows
        newChat();

        // Also clear any persistent UI state from ArtefactStore
        useArtefactStore.getState().clearAllState();
      }

      // Only reload workflow if we don't have one or it's a different workflow
      // This preserves tool modifications when starting new chats
      if (!workflow || workflow.id !== workflowId) {
        logger.info("🔄 Chat useEffect - triggering workflow load", {
          workflowId,
          hasWorkflow: String(!!workflow),
          currentWorkflowId: workflow?.id,
        });
        loadWorkflow(workflowId, adminClient).catch((error) => {
          logger.error("❌ Chat useEffect - workflow loading failed", error);
        });
      }
      // Set the current workflow ID in ChatStore to track workflow changes
      setCurrentWorkflowId(workflowId);
    }
  }, [workflowId, adminClient, loadWorkflow, setCurrentWorkflowId, workflow, newChat]);

  // Phase 8: Register callback to update URL when chat is saved
  useEffect(() => {
    logger.info("🔗 Chat: Registering onChatSaved callback");
    setOnChatSaved((chatId: string, workflowId: string) => {
      logger.info("🔗 Chat: onChatSaved callback triggered", { chatId, workflowId });
      navigateToChat(chatId, workflowId);
    });

    // Cleanup: unregister callback when component unmounts
    return () => {
      logger.info("🔗 Chat: Unregistering onChatSaved callback");
      setOnChatSaved(undefined);
    };
  }, [setOnChatSaved, navigateToChat]);

  // Phase 8: Initialize chat from URL on mount and select it in sidebar
  // This effect ONLY handles page refresh/direct URL navigation
  // When user clicks in sidebar, SidebarLayout already handles selection and loading
  // We detect this by checking if runId already matches the URL (SidebarLayout's load() ran first)
  useEffect(() => {
    // If runId is null, we're in a "new chat" state - don't select anything
    if (!runId && chatIdFromURL) {
      logger.info("🔗 Chat: Ignoring chatId in URL because runId is null (new chat)", {
        chatIdFromURL: chatIdFromURL ?? undefined,
      });

      return;
    }

    // Skip if we already handled this chat ID (prevents double-processing)
    if (lastHandledUrlChatIdRef.current === chatIdFromURL) {
      logger.info("🔗 Chat: Skipping already-handled chatId", {
        chatIdFromURL: chatIdFromURL ?? undefined,
      });

      return;
    }

    // Skip if runId already matches - means SidebarLayout already loaded this chat via click
    // This is the key check that prevents double selection on user clicks
    if (runId === chatIdFromURL) {
      logger.info("🔗 Chat: Chat already loaded by SidebarLayout, skipping", {
        chatIdFromURL: chatIdFromURL ?? undefined,
      });
      lastHandledUrlChatIdRef.current = chatIdFromURL;

      return;
    }

    // Only load chat if we have a valid chatId in URL and adminClient is ready
    // This path is for page refresh/direct URL navigation only
    if (chatIdFromURL && adminClient) {
      logger.info("🔗 Chat: Found chatId in URL (page refresh), loading chat", {
        chatIdFromURL: chatIdFromURL ?? undefined,
      });

      // Mark as handled BEFORE any async operations
      lastHandledUrlChatIdRef.current = chatIdFromURL;

      // Select the chat in the sidebar immediately (visual feedback)
      selectChat(chatIdFromURL);

      // Load the chat
      load(adminClient, chatIdFromURL)
        .then(() => {
          logger.info("✅ Chat: Chat loaded from URL", {
            chatIdFromURL: chatIdFromURL ?? undefined,
          });
        })
        .catch((error) => {
          logger.error("❌ Chat: Failed to load chat from URL", error);
        });
    }
    // Note: We don't clear selection here when chatIdFromURL is null
    // because handleNewChat in SidebarLayout handles that directly
  }, [chatIdFromURL, adminClient, load, runId, selectChat]);

  const submit = async (prompt: string, files: UploadedFile[], mentions?: StructuredMention[]) => {
    logger.info("🚀 Chat submit started", {
      hasAdminClient: String(!!adminClient),
      hasPlatformClient: String(!!platformClient),
      hasWorkflow: String(!!workflow),
      workflowId: workflow?.id,
      workflowName: workflow?.name,
      isLoadingWorkflow: String(isLoadingWorkflow),
      runId: runId ?? undefined,
      mentionCount: String(mentions?.length || 0),
      timestamp: new Date().toISOString(),
    });

    if (!adminClient) {
      throw new Error("Admin client not found");
    }
    if (!platformClient) {
      throw new Error("Platform client not found");
    }

    // Wait for workflow loading to complete if in progress
    if (isLoadingWorkflow) {
      logger.info("⏳ Waiting for workflow to load before submit...");
      let retries = 0;
      const maxRetries = 50; // 5 seconds max wait

      while (useWorkflowStore.getState().isLoadingWorkflow && retries < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        retries++;
      }
      logger.info("✅ Workflow loading wait completed", { retries });
    }

    // Get fresh workflow state after waiting
    const currentWorkflow = useWorkflowStore.getState().workflow;

    if (!currentWorkflow) {
      logger.error("❌ Workflow validation failed", undefined, {
        workflowId,
        hasWorkflowId: String(!!workflowId),
        isLoadingWorkflow: String(useWorkflowStore.getState().isLoadingWorkflow),
      });
      throw new Error("Workflow not found after loading - check console for debug details");
    }
    const userId = userProfile?.id;

    if (!userId) {
      throw new Error(
        "User profile information is missing. Please ensure you are logged in and try refreshing the page.",
      );
    }
    if (!runId) {
      if (!userProfile?.id || !userProfile?.tenant) {
        logger.error("User profile check failed", undefined, {
          hasId: String(!!userProfile?.id),
          hasTenant: String(!!userProfile?.tenant),
        });
        throw new Error(
          "User profile information is missing. Please ensure you are logged in and try refreshing the page.",
        );
      }
      logger.info("🚀 Creating workflow with validated currentWorkflow", {
        currentWorkflowId: currentWorkflow.id,
      });
      await create(platformClient, currentWorkflow, userId, userProfile.tenant, prompt);
    }

    const msg = {
      prompt,
      ...(files.length > 0
        ? {
            files: files.map((file) => ({
              name: file.name,
              type: file.type,
              size: file.size,
              id: file.id,
            })),
          }
        : {}),
      ...(mentions && mentions.length > 0 ? { mentions: JSON.stringify(mentions) } : {}),
    };

    // Log mentions for debugging
    if (mentions && mentions.length > 0) {
      logger.debug("📣 Sending message with mentions", {
        mentionCount: mentions.length,
        mentions: JSON.stringify(
          mentions.map((m) => ({
            identity_id: m.identity_id,
            display_text: m.display_text,
            resolved_name: m.resolved_name,
            target_mode: m.target_mode,
          })),
        ),
      });
    }

    await subscribeAndInvoke({
      sseWorkflowClient,
      adminClient,
      platformClient,
      userId,
      fetchChatHistory,
      message: msg,
      subscribe,
      invoke,
    });
  };

  // Don't render chat if user profile is not loaded yet
  if (!userProfile) {
    return <ChatLoadingState message={t("Loading user profile…")} />;
  }

  const showDescription =
    workflowId !== "chat_agent" &&
    workflowId !== "prompt_generator_workflow" &&
    workflow?.description &&
    (!chat || chat.messages.length === 0);

  const artefacts = getFilteredArtefactsForCurrentChat();
  const hasArtefacts = artefacts.length > 0;

  const citations = getCitationsForCurrentChat();
  const hasCitations = citations.length > 0;

  // Only show artefacts panel if there's actually content to display
  // On mobile, we'll use a modal instead of the side panel
  const shouldShowArtefactsPanel = artefactsVisible && (hasArtefacts || hasCitations) && !isMobile;

  return (
    <div ref={containerRef} className="flex h-full w-full overflow-hidden">
      {/* Main chat area */}
      <div
        className={`flex flex-col ${!isResizing ? "transition-[width] duration-300 ease-out motion-reduce:transition-none" : ""}`}
        data-testid="chat-container"
        style={{
          width: shouldShowArtefactsPanel ? `${chatWidth}%` : "100%",
          ...(isResizing ? { willChange: "width" } : {}),
        }}
      >
        <div className="flex flex-col flex-1 pl-2 pr-2 min-h-0">
          {workflow ? (
            <>
              {/* Content panel toggle for non-Invest AI brands */}
              {brand.name !== "Invest Ai" && (hasArtefacts || hasCitations) && (
                <div className="flex justify-end px-4 py-2">
                  <ArtefactsPanelToggle isVisible={artefactsVisible} onToggle={toggleArtefacts} />
                </div>
              )}

              {brand.name === "Invest Ai" && (
                <InvestAIHeaderControls
                  artefactsVisible={artefactsVisible}
                  hasContent={hasArtefacts || hasCitations}
                  isSpecialized={isSpecializedWorkflow(workflow)}
                  workflow={workflow}
                  onToggleArtefacts={toggleArtefacts}
                />
              )}

              {/* Integration Warning Banner */}
              {!dismissedIntegrationWarning && enabledToolIds.length > 0 && (
                <IntegrationWarningBanner
                  enabledToolIds={enabledToolIds}
                  onDismiss={() => setDismissedIntegrationWarning(true)}
                />
              )}

              {/* Workflow Description Bubble */}
              {showDescription && workflow && (
                <WorkflowDescriptionBubble
                  description={workflow.description || ""}
                  name={workflow.name}
                />
              )}

              <div className="flex-1 overflow-auto min-h-0">
                {chat && chat.messages.length > 0 && (
                  <ChatPanel messages={chat.messages} runId={runId || ""} />
                )}
              </div>

              {/* Prompt area resize handle - hidden on mobile */}
              {!isMobile && (
                <PromptResizeHandle
                  isResizing={isResizingPrompt}
                  valueMax={maxPromptHeight}
                  valueMin={MIN_PROMPT_HEIGHT}
                  valueNow={promptHeight}
                  onDoubleClick={resetPromptHeight}
                  onKeyboardResize={handlePromptResizeKeyboard}
                  onMouseDown={handlePromptResizeMouseDown}
                />
              )}

              {/* Prompt area wrapper with controlled height */}
              <div
                ref={promptWrapperRef}
                className="flex flex-col shrink-0"
                style={{
                  ...(promptHeight != null ? { height: `${promptHeight}px` } : {}),
                  ...(isResizingPrompt ? { willChange: "height" } : {}),
                }}
              >
                {/*
                  All brands now use the unified PromptWithActions.
                  PromptWithActions-InvestAI.tsx remains on disk for reference but is no longer
                  referenced here — brand-specific styling is expressed via CSS tokens.
                  Visibility is gated by userProfile.config permissions (hard gate) and then
                  further filtered by simpleChatInput / pinnedChatControls inside the component.
                */}
                <PromptWithActions
                  expandToFill={promptHeight != null}
                  features={featuresCategories}
                  messagesCount={chat?.messages?.length || 0}
                  // Brand decision resolved here (parent) so the composer stays
                  // brand-agnostic: InvestAI hides the model selector.
                  showChatModelSelector={brand.name !== "Invest Ai"}
                  showFileUpload={true}
                  showKnowledgeHub={userProfile.config?.knowledgeHub}
                  showPrompto={true}
                  showPrompts={true}
                  showSearch={true}
                  showSettings={userProfile.config?.settings}
                  showVoice={true}
                  streaming={streaming}
                  submit={submit}
                  suggestions={suggestions}
                />
              </div>
            </>
          ) : (
            <ChatLoadingState message={t("Loading Agents…")} />
          )}
        </div>
      </div>

      {/* Resize Handle */}
      {shouldShowArtefactsPanel && (
        <ResizeHandle
          isResizing={isResizing}
          onKeyboardResize={handleKeyboardResize}
          onMouseDown={handleMouseDown}
        />
      )}

      {/* Content panel (Citations and Artefacts) - Desktop/Tablet */}
      {shouldShowArtefactsPanel && (
        <div
          className={`${!isResizing ? "transition-[width] duration-300 ease-out motion-reduce:transition-none" : ""} overflow-hidden ${
            isTablet ? "fixed right-0 top-0 h-full w-[min(384px,85vw)] z-50 shadow-2xl" : ""
          }`}
          style={
            isTablet
              ? {} // Use fixed positioning for tablet overlay
              : {
                  width: `${100 - chatWidth}%`,
                  minWidth: `${MIN_CHAT_WIDTH}%`,
                  ...(isResizing ? { willChange: "width" } : {}),
                }
          }
        >
          <ArtefactsPanel />
        </div>
      )}

      {/* Mobile Modal for Artefacts */}
      {isMobile && (
        <ArtefactModal
          isOpen={artefactsVisible && (hasArtefacts || hasCitations)}
          onOpenChange={(open) => {
            if (!open) toggleArtefacts();
          }}
        />
      )}

      {/* Backdrop for tablet overlay mode */}
      {isTablet && shouldShowArtefactsPanel && (
        <div
          aria-label={t("Close artefacts panel")}
          className="fixed inset-0 bg-foreground/20 z-40"
          role="button"
          tabIndex={0}
          onClick={toggleArtefacts}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleArtefacts();
            }
          }}
        />
      )}
    </div>
  );
};
